import { Capacitor } from '@capacitor/core';
import type { PushNotificationSchema } from '@capacitor/push-notifications';
import { appMetadata } from '../utils/buildVersion';
import { logger } from '../utils/logger';
import { resolveAppUrlPath } from '../utils/appLinks';
import { supabase } from '../utils/supabaseClient';

const PUSH_TOKEN_STORAGE_KEY = 'dice1k_push_token';
const PUSH_ACTIONS_STORAGE_KEY = 'dice1k_pending_push_actions';
const PUSH_PROVIDER = 'fcm';
const MAX_PENDING_PUSH_ACTIONS = 10;

export type NotificationLanguage = 'pl' | 'en';
type PendingPushActionType = 'friend_request_accept' | 'friend_request_reject';

export interface PendingPushAction {
  type: PendingPushActionType;
  requestId: string;
  createdAt: string;
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function normalizePushToken(value: string | null | undefined): string | null {
  const normalized = (value || '').trim();
  return normalized || null;
}

function normalizeNotificationLanguage(value: string | null | undefined): NotificationLanguage {
  const normalized = (value || '').trim().toLowerCase();
  return normalized.startsWith('pl') ? 'pl' : 'en';
}

function readStorageValue(key: string): string | null {
  return getStorage()?.getItem(key) ?? null;
}

function normalizeNavigationPath(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('/')) return trimmed;
  return resolveAppUrlPath(trimmed);
}

function parsePendingPushActions(raw: string | null): PendingPushAction[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((item): PendingPushAction[] => {
      if (!item || typeof item !== 'object') return [];
      const candidate = item as Record<string, unknown>;
      const type = candidate.type;
      const requestId = candidate.requestId;
      const createdAt = candidate.createdAt;
      if (
        (type !== 'friend_request_accept' && type !== 'friend_request_reject')
        || typeof requestId !== 'string'
        || !requestId.trim()
      ) {
        return [];
      }
      return [{
        type,
        requestId: requestId.trim(),
        createdAt: typeof createdAt === 'string' && createdAt.trim() ? createdAt : new Date().toISOString(),
      }];
    });
  } catch {
    return [];
  }
}

function writePendingPushActions(actions: PendingPushAction[]): void {
  const storage = getStorage();
  if (!storage) return;
  if (actions.length === 0) {
    storage.removeItem(PUSH_ACTIONS_STORAGE_KEY);
    return;
  }
  storage.setItem(PUSH_ACTIONS_STORAGE_KEY, JSON.stringify(actions.slice(-MAX_PENDING_PUSH_ACTIONS)));
}

export function readStoredPushToken(): string | null {
  return normalizePushToken(readStorageValue(PUSH_TOKEN_STORAGE_KEY));
}

export function persistPushToken(pushToken: string): void {
  const normalized = normalizePushToken(pushToken);
  if (!normalized) return;
  getStorage()?.setItem(PUSH_TOKEN_STORAGE_KEY, normalized);
}

export function clearStoredPushToken(): void {
  getStorage()?.removeItem(PUSH_TOKEN_STORAGE_KEY);
}

export function getDeviceNotificationLanguage(): NotificationLanguage {
  if (typeof navigator === 'undefined') return 'en';
  const languageCandidate = Array.isArray(navigator.languages) && navigator.languages.length > 0
    ? navigator.languages[0]
    : navigator.language;
  return normalizeNotificationLanguage(languageCandidate);
}

export function resolvePushNotificationPath(
  notification: Pick<PushNotificationSchema, 'data' | 'link'> | { data?: unknown; link?: unknown } | null | undefined,
): string | null {
  if (!notification) return null;

  const data = notification.data && typeof notification.data === 'object'
    ? notification.data as Record<string, unknown>
    : null;

  return normalizeNavigationPath(data?.path)
    || normalizeNavigationPath(data?.deepLink)
    || normalizeNavigationPath(data?.url)
    || normalizeNavigationPath(notification.link);
}

export function extractPendingPushAction(
  notification: Pick<PushNotificationSchema, 'data'> | { data?: unknown } | null | undefined,
  actionId: string | null | undefined,
): PendingPushAction | null {
  const normalizedActionId = (actionId || '').trim();
  if (normalizedActionId !== 'friend_request_accept' && normalizedActionId !== 'friend_request_reject') {
    return null;
  }

  const data = notification?.data && typeof notification.data === 'object'
    ? notification.data as Record<string, unknown>
    : null;
  const requestId = typeof data?.requestId === 'string' ? data.requestId.trim() : '';
  if (!requestId) return null;

  return {
    type: normalizedActionId,
    requestId,
    createdAt: new Date().toISOString(),
  };
}

export function readPendingPushActions(): PendingPushAction[] {
  return parsePendingPushActions(readStorageValue(PUSH_ACTIONS_STORAGE_KEY));
}

export function persistPendingPushAction(action: PendingPushAction): void {
  const existing = readPendingPushActions();
  const deduped = existing.filter((item) => !(item.type === action.type && item.requestId === action.requestId));
  deduped.push(action);
  writePendingPushActions(deduped);
}

export function consumePendingPushActions(): PendingPushAction[] {
  const actions = readPendingPushActions();
  writePendingPushActions([]);
  return actions;
}

function getCurrentPushPlatform(): 'ios' | 'android' | 'web' {
  const platform = Capacitor.getPlatform?.();
  if (platform === 'ios' || platform === 'android') return platform;
  return 'web';
}

export async function upsertMyPushDevice(pushToken: string): Promise<void> {
  const normalizedPushToken = normalizePushToken(pushToken);
  if (!normalizedPushToken) {
    return;
  }

  persistPushToken(normalizedPushToken);

  const { error } = await supabase.rpc('upsert_my_push_device', {
    platform_param: getCurrentPushPlatform(),
    provider_param: PUSH_PROVIDER,
    push_token_param: normalizedPushToken,
    app_build_param: appMetadata.buildVersion,
    enabled_param: true,
    notification_language_param: getDeviceNotificationLanguage(),
  });

  if (error) {
    logger.warn('PUSH_DEVICE_UPSERT_FAILED', {
      message: error.message,
      platform: getCurrentPushPlatform(),
    });
    throw new Error(error.message);
  }
}

export async function disableStoredPushDevice(): Promise<void> {
  const storedPushToken = readStoredPushToken();
  if (!storedPushToken) return;

  const { error } = await supabase.rpc('disable_my_push_device', {
    push_token_param: storedPushToken,
  });

  if (error) {
    logger.warn('PUSH_DEVICE_DISABLE_FAILED', {
      message: error.message,
      pushToken: storedPushToken,
    });
    throw new Error(error.message);
  }

  clearStoredPushToken();
}
